import { LocationType } from '~/client/graph'
import {
  ILbsLevel,
  IMapViewItem,
  IMapViewPublishedTo,
  MapViewCapability,
} from '~/client/src/shared/components/MapViews/MapViewsSidebar/MapViewsSidebarListItem'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import GlobeView from '~/client/src/shared/models/GlobeView'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import Sitemap from '~/client/src/shared/models/Sitemap'

// Scope rules: bare-project when no meaningful owner; otherwise first meaningful
// owner is the representative (drives iconName + color), names joined with ", ".

const SCOPE_SEPARATOR = ', '

interface ILocationAttributesProjection {
  readonly allAttributes: readonly LocationBase[]
  getAncestryChain(attribute: LocationBase): readonly LocationBase[]
}

interface IBuildMapViewItemArgs {
  globe?: GlobeView
  sitemap?: Sitemap
  locationAttributesStore: ILocationAttributesProjection
  /** Omit to hide the publish-to chip row (non-admin / read-only). */
  publishedTo?: (mapId: string) => IMapViewPublishedTo
}

export type MapViewSource =
  | { readonly kind: 'globe'; readonly map: GlobeView }
  | { readonly kind: 'sitemap'; readonly map: Sitemap }

interface IBuildMapViewItemsArgs {
  readonly sources: readonly MapViewSource[]
  readonly locationAttributesStore: ILocationAttributesProjection
  readonly publishedTo?: (source: MapViewSource) => IMapViewPublishedTo
}

/** Builds sidebar rows while indexing map ownership once for the collection. */
export function buildMapViewItems({
  sources,
  locationAttributesStore,
  publishedTo,
}: IBuildMapViewItemsArgs): IMapViewItem[] {
  const ownersByGlobeId = new Map<string, LocationBase[]>()
  const ownersBySitemapId = new Map<string, LocationBase[]>()

  for (const attribute of locationAttributesStore.allAttributes) {
    if (attribute.type === LocationType.ProjectLocation) continue
    indexOwner(ownersByGlobeId, attribute.assignedGlobes, attribute)
    indexOwner(ownersBySitemapId, attribute.assignedSitemaps, attribute)
  }

  return sources.map(source => {
    const owners =
      source.kind === 'globe'
        ? (ownersByGlobeId.get(source.map.id) ?? [])
        : (ownersBySitemapId.get(source.map.id) ?? [])
    return buildItem(
      source,
      owners,
      locationAttributesStore,
      publishedTo?.(source),
    )
  })
}

/**
 * Builds one sidebar row. Prefer buildMapViewItems for collections.
 * @public
 */
export function buildMapViewItem(
  args: IBuildMapViewItemArgs,
): IMapViewItem | null {
  const { globe, sitemap, publishedTo } = args
  const source: MapViewSource | undefined = globe
    ? { kind: 'globe', map: globe }
    : sitemap
      ? { kind: 'sitemap', map: sitemap }
      : undefined
  if (!source) return null

  return buildMapViewItems({
    sources: [source],
    locationAttributesStore: args.locationAttributesStore,
    publishedTo: publishedTo
      ? current => publishedTo(current.map.id)
      : undefined,
  })[0]
}

function buildItem(
  source: MapViewSource,
  meaningfulOwners: readonly LocationBase[],
  locationAttributesStore: ILocationAttributesProjection,
  publishedTo?: IMapViewPublishedTo,
): IMapViewItem {
  const map = source.map
  const representative = meaningfulOwners[0]

  const scopeLabel =
    meaningfulOwners.length > 0
      ? meaningfulOwners.map(o => o.name).join(SCOPE_SEPARATOR)
      : Localization.translator.project

  return {
    id: map.id,
    name: map.name,
    filledImage: map.filledImage,
    scopeLabel,
    scopeIconName: representative?.iconName ?? MapViewLocationIcon.Project,
    scopeColor: representative?.color,
    lbsAncestry: representative
      ? locationAttributesStore.getAncestryChain(representative).map(toLbsLevel)
      : [],
    capability: resolveCapability(source),
    createdAt: map.createdAt,
    updatedAt: map.updatedAt,
    publishedTo,
  }
}

function indexOwner(
  ownersByMapId: Map<string, LocationBase[]>,
  mapIds: readonly string[] | undefined,
  owner: LocationBase,
): void {
  for (const mapId of new Set(mapIds ?? [])) {
    const owners = ownersByMapId.get(mapId)
    if (owners) {
      owners.push(owner)
    } else {
      ownersByMapId.set(mapId, [owner])
    }
  }
}

const toLbsLevel = (loc: {
  id: string
  name: string
  iconName: MapViewLocationIcon
  color: string
}): ILbsLevel => ({
  id: loc.id,
  label: loc.name,
  iconName: loc.iconName,
  color: loc.color,
})

function resolveCapability(source: MapViewSource): MapViewCapability {
  if (source.kind === 'sitemap') {
    return source.map.isReferenced ? 'georeferenced' : 'whiteboard'
  }
  const isGeoreferenced = !!source.map.bounds || !!source.map.center
  return isGeoreferenced ? 'georeferenced' : 'whiteboard'
}
